// Onboarding gate — signed-in user with no team org.
//
// Per MODEL.md, workspace is an org-level feature: a user with no team org has
// nothing to scope to (empty workspace_id claim → RLS returns zero rows on
// every tenant table), so the workspace has nothing to render.
//
// This USED to render a full-page fixed overlay with an inline create-org form.
// It's now a HIDDEN gate that opens the shared `AppCreateOrgModal` (in
// onboarding mode) through @symbo.ls/windows when the condition holds (2026-06-17
// per Nika "NoOrgOverlay should be moved as @symbo.ls/windows modal"). The modal
// offers two choices: "Create workspace" or "Use personal workspace" (the escape
// that persists a skip so the gate never re-opens). All form / slug / submit
// logic now lives in `AppCreateOrgModal.js` + `createOrgModal.js` — single
// source, no duplicate inline form.

import { isOrgOnboardingSkipped } from '../functions/createOrgModal.js'
import { isUsernamePromptPendingPersisted } from '../functions/usernamePrompt.js'
import { USE_ONBOARDING_GATE } from '../functions/featureFlags.js'
import { isCustomDomainHost } from '../functions/customDomainHost.js'

// "Does the signed-in user belong to any team org?" — checked against BOTH
// sources: `root.orgs` (SDK Mongo /auth/me, canonical) and `jwtClaims.orgs`
// (federated Supabase JWT). SDK-Mongo tokens never carry orgs[] in the JWT, so
// checking only jwtClaims gave a false negative for every Mongo-authed user.
// Personal-only users return false → the gate fires.
const _hasTeamOrg = (root) => {
  const fromState = Array.isArray(root?.orgs) ? root.orgs : null
  const fromClaims = Array.isArray(root?.jwtClaims?.orgs) ? root.jwtClaims.orgs : null
  const orgs = fromState && fromState.length ? fromState : fromClaims || []
  if (!orgs.length) return false
  return orgs.some((o) => o?.type == null || o.type === 'team')
}

// Gate predicate. `_authReady` / `signedIn` / `jwtClaims` keep the modal from
// opening before the auth snapshot has seeded (else it would flash for every
// signed-in user between mount and subscribeAuthSession's first fire).
// INVITE SUPPRESSION (INVITE-ONBOARDING-USERNAME-NOT-ORG-1): a user inside
// an invite-acceptance flow must NEVER meet org creation — they are joining
// an existing org, and the no-team-org condition is merely the acceptance
// not having landed yet. `_inviteFlowActive` covers the accept page's
// window; the username-prompt pending markers cover the post-accept beat
// (their own gate shows the light username nudge instead).
const _shouldGate = (el, root) => {
  if (!USE_ONBOARDING_GATE) return false
  // WHITELABEL SUPPRESSION (Nika 2026-08-30: "if I'm joining bellforge —
  // it should not ask me to create workspace"). On a white-label host the
  // visitor is by definition joining the HOST org — offering to found a
  // Symbols org there is the platform's onboarding leaking into a
  // customer's own domain. Same category as the invite suppression below:
  // the no-team-org condition is real, but org creation is never the
  // answer on this host. Platform hosts are untouched (isCustomDomainHost
  // is false on my.symbols.app / localhost / SSR).
  if (isCustomDomainHost()) return false
  if (!root?._authReady || !root?.signedIn || !root?.jwtClaims) return false
  // THE MODAL HOST FIRST (WS-SHELL-MOUNT-TREE-BUILD-2700MS-1): `ModalRoot`
  // mounts behind the `_modalHostReady` latch, flipped after the first paint.
  // A cold boot whose auth is already seeded (a hand-over arrival:
  // magic-callback → the workspace) reached this gate before the latch
  // flipped, pushed the layer onto a host not built yet, and the modal's
  // custom content mounted DETACHED — a blank, non-dismissable veil over the
  // home, no way out (measured on dev 2026-10-24, workspace e96e3b234, a Fly
  // sign-up's first arrival; reopened after boot the same modal renders).
  if (!root?._modalHostReady) return false
  if (root?._orgOnboardingSkipped || isOrgOnboardingSkipped()) return false
  if (root?._inviteFlowActive || root?._usernamePromptPending || isUsernamePromptPendingPersisted())
    return false
  // ARRIVED FROM FLY (fly docs/FUNNEL.md §4 G5: the shell never repeats what
  // Fly already did). Make it real on fly.symbols.app made this person's
  // workspace — the personal one, or a new one they chose — and wrote
  // `user_preferences.onboarding = { via: 'fly' }` before the hand-over;
  // asking them again to found a team org or keep the personal workspace is
  // Fly's own question twice. The record loads AFTER the boot loader (a
  // non-blocking read), so the gate waits for it rather than flash at a Fly
  // arrival; a read that answered nothing is `{}` (functions/loadPrivateData.js).
  const prefs = root?.userPrefs
  if (!prefs || typeof prefs !== 'object') return false
  // Fly's record says so while its welcome is pending or done (`via: 'fly'`);
  // once the person took the tour from the ready screen the decision says
  // `via: 'tour'` and keeps `origin: 'fly'` (functions/onboarding.js
  // onboardingDecide) — the org question stays Fly's either way.
  const welcome = prefs.onboarding
  if (welcome && (welcome.via === 'fly' || welcome.origin === 'fly')) return false
  // THE WELCOME OWNS THE FIRST WORKSPACE (round 2 BLOCKER #5, elena-r2 +
  // sam-r2): a direct sign-up's one personal workspace holds the sign-up
  // credits and the new-account welcome opens on it. This modal opened by
  // itself over the welcome (on a step, on a reload); the person founded a
  // second organisation with an empty, 0-credit workspace. For a new account,
  // or anyone the welcome already met, the welcome names and sets up THAT
  // workspace (functions/createOrgModal.js orgOnboardingOwnedByWelcome).
  if (el.call('orgOnboardingOwnedByWelcome', root)) return false
  return !_hasTeamOrg(root)
}

// Open the onboarding modal at most once per mount. With the modal
// non-dismissable (dismissOnBackdrop:false + ESC blocked), it can only leave
// via "Create workspace" (hard reload) or "Use personal workspace" (persists
// the skip), so a single open is correct.
const _maybeOpen = (el, s) => {
  if (el.__onboardingOpened) return
  if (!_shouldGate(el, s.root)) return
  el.__onboardingOpened = true
  el.call('openOnboardingWorkspaceModal')
}

export const NoOrgOverlay = {
  tag: 'div',
  display: 'none',
  // Auth claims / org list often land AFTER mount on SDK-only sessions — watch
  // them (+ the skip flag) so the gate re-evaluates when they arrive.
  subscribeTo: [
    'jwtClaims',
    'orgs',
    'signedIn',
    '_authReady',
    '_orgOnboardingSkipped',
    '_inviteFlowActive',
    '_usernamePromptPending',
    // the modal host's latch and the person's record (see _shouldGate)
    '_modalHostReady',
    'userPrefs',
    // the welcome's own facts (see orgOnboardingOwnedByWelcome)
    'user',
    '_onboardingDecision'
  ],
  onCreate: (el, s) => _maybeOpen(el, s),
  onUpdate: (el, s) => _maybeOpen(el, s)
}
